import React from "react";
import styled from "@emotion/styled";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { ThemeContext, UserContext, LanguageContext } from "../contexts/Context";
import MenuItemDisplay3 from "./MenuItemDisplay3";

const RecentBoughtItems = ({
    width,
    menuSelectionFormRef,
    selectedItems,
    theme,
    sidebarWidth,
    headerHeight,
    setSelectedMenu,
    lanIndex,
    addRecentItem
}) => {
    const themeContext = React.useContext(ThemeContext);
    const languageContext = React.useContext(LanguageContext);
    const { recentItem, currencySymbol, isLanReverse } = React.useContext(UserContext);
    const recentListRef = React.useRef(null);
    const [scrollState, setScrollState] = React.useState({ canScrollLeft: false, canScrollRight: false });
    const recentItemCount = recentItem ? recentItem.length : 0;

    const updateScrollState = React.useCallback(() => {
        const list = recentListRef.current;
        if (!list) return;
        const maxScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth);
        setScrollState({
            canScrollLeft: list.scrollLeft > 2,
            canScrollRight: list.scrollLeft < maxScrollLeft - 2
        });
    }, []);

    React.useEffect(() => {
        const list = recentListRef.current;
        if (!list) return undefined;

        updateScrollState();
        list.addEventListener("scroll", updateScrollState);
        window.addEventListener("resize", updateScrollState);

        return () => {
            list.removeEventListener("scroll", updateScrollState);
            window.removeEventListener("resize", updateScrollState);
        };
    }, [recentItemCount, updateScrollState]);

    const scrollRecentItems = (direction) => {
        const list = recentListRef.current;
        if (!list) return;
        list.scrollBy({
            left: direction * Math.min(680, list.clientWidth * 0.72),
            behavior: "smooth"
        });
    };


    return (
        <RecContainer themeContext={themeContext}>
            {recentItem && recentItem.length > 0 &&
                <RecTitle themeContext={themeContext}>
                    <TitleLabel>
                        {languageContext[lanIndex].recentBought}
                    </TitleLabel>
                    <AddAllButton type="button" themeContext={themeContext} onClick={() => addRecentItem()}>
                        {languageContext[lanIndex].addAll}
                    </AddAllButton>
                </RecTitle>
            }
            {recentItem && recentItem.length > 0 &&
                <RecentRail>
                    <ScrollButton
                        type="button"
                        side="left"
                        themeContext={themeContext}
                        disabled={!scrollState.canScrollLeft}
                        onClick={() => scrollRecentItems(-1)}
                        aria-label="Previous recently bought items"
                    >
                        <IoIosArrowBack size={24} />
                    </ScrollButton>
                <RecWrapper ref={recentListRef}>
                    {recentItem.map((it, i) =>
                        <RecentItem key={it.codeId || i}>
                            <MenuItemDisplay3
                                lanIndex={lanIndex}
                                isLanReverse={isLanReverse}
                                item={it}
                                selectedItems={selectedItems}
                                setSelectedMenu={setSelectedMenu}
                                currencySymbol={currencySymbol}
                                menuSelectionFormRef={menuSelectionFormRef}
                                recentBoughtCard

                            />
                        </RecentItem>
                    )}
                </RecWrapper>
                    <ScrollButton
                        type="button"
                        side="right"
                        themeContext={themeContext}
                        disabled={!scrollState.canScrollRight}
                        onClick={() => scrollRecentItems(1)}
                        aria-label="Next recently bought items"
                    >
                        <IoIosArrowForward size={24} />
                    </ScrollButton>
                </RecentRail>
            }
        </RecContainer>
    );


};

export default RecentBoughtItems;

const RecTitle = styled.div`
    display: flex;
    align-items: center;
    font-family: ${(props) => props.themeContext.fontFamily};
    font-size: 16px;
    font-weight: 600;
    text-align: left;
    margin-left: 16px;
    margin-right: 16px;

    @media (min-width: 768px) {
        margin: 0 2px 16px;
        color: ${(props) => props.themeContext.fontPrimary};
        font-size: 20px;
    }
`;
const RecContainer = styled.div`
    display:block;
    width: 100%;
    margin-top: 32px;
    padding-top: 16px;

    @media (min-width: 768px) {
        width: calc(100% - 36px);
        margin: 22px 18px 10px;
        padding: 20px 18px 22px;
        box-sizing: border-box;
        border: 1px solid ${(props) => props.themeContext.borderColor || "rgba(15, 23, 42, 0.1)"};
        border-radius: 20px;
        background: ${(props) => props.themeContext.backgroundColor};
        box-shadow: 0 12px 34px rgba(15, 23, 42, 0.1);
    }
`;
const TitleLabel = styled.div`
    flex-grow: 1;
`;
const AddAllButton = styled.button`
    padding: 0;
    border: 0;
    color: inherit;
    background: transparent;
    font: inherit;
    font-size: 14px;
    text-decoration: underline;
    cursor: pointer;

    @media (min-width: 768px) {
        min-height: 38px;
        padding: 0 16px;
        border: 1px solid ${(props) => props.themeContext.borderColor || "rgba(15, 23, 42, 0.14)"};
        border-radius: 999px;
        color: ${(props) => props.themeContext.fontPrimary};
        background: ${(props) => props.themeContext.backgroundColor};
        font-size: 14px;
        font-weight: 700;
        text-decoration: none;
        box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
        transition: transform 150ms ease, box-shadow 150ms ease;

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 7px 18px rgba(15, 23, 42, 0.14);
        }

        &:focus-visible {
            outline: 3px solid rgba(59, 130, 246, 0.3);
            outline-offset: 2px;
        }
    }
`;
const RecentRail = styled.div`
    position: relative;
    width: 100%;
`;
const RecWrapper = styled.div`
    display: flex;
    padding-bottom: 16px;
    overflow-y: hidden;
    overflow-x: auto;
    position: relative;
    padding-left: 8px;
    padding-right: 8px;

    @media (min-width: 768px) {
        gap: 14px;
        padding: 2px 4px 8px;
        scroll-snap-type: x proximity;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }
`;
const RecentItem = styled.div`
    display: contents;

    @media (min-width: 768px) {
        display: flex;
        flex: 0 0 clamp(270px, 28vw, 350px);
        min-width: 0;
        scroll-snap-align: start;
    }
`;
const ScrollButton = styled.button`
    display: none;

    @media (min-width: 768px) {
        position: absolute;
        top: 50%;
        ${(props) => props.side}: -8px;
        z-index: 4;
        display: flex;
        width: 40px;
        height: 40px;
        padding: 0;
        align-items: center;
        justify-content: center;
        border: 1px solid ${(props) => props.themeContext.borderColor || "rgba(15, 23, 42, 0.14)"};
        border-radius: 50%;
        color: ${(props) => props.themeContext.fontPrimary};
        background: ${(props) => props.themeContext.backgroundColor};
        box-shadow: 0 7px 20px rgba(15, 23, 42, 0.18);
        transform: translateY(-50%);
        cursor: pointer;
        transition: opacity 150ms ease, transform 150ms ease;

        &:hover:not(:disabled) {
            transform: translateY(-50%) scale(1.06);
        }

        &:disabled {
            opacity: 0;
            pointer-events: none;
        }
    }
`;
